import dayjs from 'dayjs';

import { IContactGroupAddress } from 'contacts/types';
import { ExternalProjectRecordType } from 'projects/enums';
import { IProjectAutocomplete } from 'projects/types';
import { AttributeProjectKind, SpaStatus } from 'quotes/enums';
import { IAttributeCustomer, IAttributeProject, ISpaAudience } from 'quotes/types';

import { getAttributeDefaultValues } from './defaults';
import { SpaPriorityTier } from '../../enums';
import { IAttributeForm, IAttributeFormLocation, IAttributes } from '../../types';

const PROJECT_RECORD_TYPES: string[] = [
  ExternalProjectRecordType.PROJECT,
  ExternalProjectRecordType.FUNDED_PROJECT,
  ExternalProjectRecordType.UNFUNDED_PROJECT,
];

type TProjectKindSource = Pick<
  IProjectAutocomplete,
  'id' | 'externalProgramNumber' | 'externalProjectNumber' | 'externalRecordType'
>;

// Mirrors the BE kind rule: a known record type wins, else the numbers decide (project number ⇒
// project). Anything else (program number alone, or no type and no number) is sent as All
// Customers by the BE, so it is treated as a program: it needs the All Customers right and its
// customers are never derived. Null only without a project.
export const getAttributeProjectKind = (
  project: TProjectKindSource | null | undefined
): AttributeProjectKind | null => {
  if (!project?.id) {
    return null;
  }

  const recordType = project.externalRecordType ?? '';

  if (ExternalProjectRecordType.PROGRAM === recordType) {
    return AttributeProjectKind.PROGRAM;
  }

  if (PROJECT_RECORD_TYPES.includes(recordType) || project.externalProjectNumber) {
    return AttributeProjectKind.PROJECT;
  }

  return AttributeProjectKind.PROGRAM;
};

// Rebuilds the picker fields the kind rule reads, so a hydrated project resolves to the kind the
// BE returned.
const mapAttributeProjectToForm = (project: IAttributeProject): IProjectAutocomplete => {
  const recordTypeByKind: Record<AttributeProjectKind, ExternalProjectRecordType> = {
    [AttributeProjectKind.PROGRAM]: ExternalProjectRecordType.PROGRAM,
    [AttributeProjectKind.PROJECT]: ExternalProjectRecordType.PROJECT,
  };

  return {
    id: project.id,
    name: project.name ?? '',
    externalProjectNumber: project.projectNumber ?? undefined,
    externalProgramNumber: project.programNumber ?? undefined,
    // A null kind is sent as All Customers, so it hydrates as a program.
    externalRecordType: recordTypeByKind[project.kind ?? AttributeProjectKind.PROGRAM],
  };
};

export const mapAttributeCustomersToForm = (
  customers: IAttributeCustomer[] | null | undefined
): IContactGroupAddress[] =>
  (customers ?? []).map(
    (customer) =>
      ({
        id: customer.id,
        name: customer.name ?? '',
        accountsReceivableId: customer.accountsReceivableId ?? undefined,
      }) as IContactGroupAddress
  );

export const mapAttributeFormToSpaAudience = (
  form: Pick<IAttributeForm, 'allCustomers' | 'allSalesLocations' | 'project'>
): ISpaAudience => ({
  allCustomers: !!form.allCustomers,
  allSalesLocations: !!form.allSalesLocations,
  projectKind: getAttributeProjectKind(form.project),
});

export type AttributePriorityMode = 'project' | 'customers' | null;

export const getPriorityModeFromConfig = (
  attributes: IAttributes | null
): AttributePriorityMode => {
  if (attributes?.project?.id) {
    return 'project';
  }

  if (attributes?.allCustomers || (attributes?.customers?.length ?? 0) > 0) {
    return 'customers';
  }

  return null;
};

// Same derivation over in-progress form values instead of the saved config.
export const getPriorityModeFromForm = (form: IAttributeForm): AttributePriorityMode => {
  if (form.project?.id) {
    return 'project';
  }

  if (form.allCustomers || (form.customers?.length ?? 0) > 0) {
    return 'customers';
  }

  return null;
};

const getDefaultTierForMode = (mode: AttributePriorityMode): SpaPriorityTier => {
  if ('project' === mode) {
    return SpaPriorityTier.PRIORITY_1;
  }

  return SpaPriorityTier.PRIORITY_3;
};

/**
 * Build RHF form values from the saved attributes (static defaults when nothing is saved).
 * The tier falls back to the mode default when the attributes have a selection but no tier,
 * since the schema requires one and the BE may persist a null tier.
 */
export const mapAttributesToForm = (attributes: IAttributes | null): IAttributeForm => {
  const defaults = getAttributeDefaultValues();

  if (!attributes) {
    return defaults;
  }

  const mode = getPriorityModeFromConfig(attributes);

  const project: IProjectAutocomplete | null = attributes.project?.id
    ? mapAttributeProjectToForm(attributes.project)
    : null;

  // For a project the read returns the live derived set, which is shown locked, not picked.
  const isProjectKind = AttributeProjectKind.PROJECT === getAttributeProjectKind(project);
  const storedCustomers = mapAttributeCustomersToForm(attributes.customers);

  // The chips render the location id, and the first sales location seeds the Trade Agreement
  // Owner — both have to survive hydration, not just id + name.
  const mapLocations = (locations: IAttributes['salesLocations']): IAttributeFormLocation[] =>
    locations.map((location) => ({
      id: location.id,
      name: location.name ?? '',
      externalLocationId: location.externalLocationId ?? '',
      tradeAgreementOwner: location.tradeAgreementOwner,
    }));

  const salesLocations = mapLocations(attributes.salesLocations ?? []);

  return {
    project,
    projectCustomers: isProjectKind ? storedCustomers : [],
    customers: isProjectKind ? [] : storedCustomers,
    allCustomers: attributes.allCustomers,
    allSalesLocations: attributes.allSalesLocations,
    salesLocations,
    selectableSpaEnabled: attributes.selectableSpaEnabled ?? false,
    shippingLocations: mapLocations(attributes.shippingLocations ?? []),
    spaPriorityTier: attributes.priorityTier ?? getDefaultTierForMode(mode),
    // Hydrated, never defaulted over: the export drawer sends this back, so a Live quote read as
    // Planning would be reset on the next Preview or Download. Null (never saved) preselects
    // Planning, which the first save then writes.
    spaStatus:
      attributes.spaStatus && Object.values(SpaStatus).includes(attributes.spaStatus)
        ? attributes.spaStatus
        : defaults.spaStatus,
    // Same chain the BE resolves the export column with: saved owner, else the first sales
    // location's owner (there is none under "All Locations", which persists no locations).
    tradeAgreementOwner:
      attributes.tradeAgreementOwner ?? salesLocations[0]?.tradeAgreementOwner ?? null,
    // No fallback on purpose — an unset date must stay unset rather than
    // defaulting to today, which is what the export used to do.
    tradeAgreementStartDate: attributes.tradeAgreementStartDate
      ? dayjs(attributes.tradeAgreementStartDate)
      : null,
  };
};
